// Global
import React, { useEffect, useState } from 'react';
import { Field, ImageField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { tv } from 'tailwind-variants';
import classNames from 'classnames';
import {
  FilterAnyOf,
  WidgetRequest,
  Request,
  DataProvider,
  SearchResponse,
} from '@sitecore-search/react';
import { usePathname } from 'next/navigation';

// Lib
import { formatTimestamp } from 'lib/utils/date-utils';
import { useDictionary } from 'lib/hooks/use-dictionary';
import { ComponentProps } from 'lib/component-props';
import { SitecoreIds } from 'lib/constants/sitecore-ids';
import { LOCALE_CONFIG, SupportedLanguage } from 'lib/constants/locale-constant';

//helpers
import Button from 'helpers/Button/Button';
import WidgetWrapper from 'helpers/WidgetWrapper/WidgetWrapper';
import SVG from 'helpers/SVG/SVG';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';

// Local
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import { replaceTokens, SourceObject, TokenObject } from 'lib/utils/token-replacer';

type CategoryType = 'category-1' | 'category-2' | 'category-3' | 'category-4' | undefined;
export type DisplayOptions = { Value: Field<string> };

type PageTypes = 'Category';

export type RelatedResourcesProps = Omit<ComponentProps, 'params'> & {
  uid: string;
  componentName: string;
  dataSource: string;
  params: Params;
  fields: Fields;
};

export type Params = {
  DisplayOptions: DisplayOptions;
  ImageBleed: string;
  dataSource: string;
};

export type Fields = {
  Headline: Field<string>;
  Hero: ImageField;
  HeroTablet: ImageField;
  ReadTime: Field<string>;
  PublishDate: Field<string>;
  Teaser: Field<string>;
  ContentType: ContentTypePropItem;
  Category: CategoryItemProps[];
  VideoUrl: Field<string>;
  url: Field<string>;
};

export type ContentTypePropItem = {
  name: string;
  url: string;
  displayName: string;
  fields: {
    Name: Field<string>;
  };
};

export type ColorItemProps = {
  id?: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields: {
    Value: Field<string>;
  };
};

export type CategoryItemProps = {
  id?: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields: {
    Color: ColorItemProps;
  };
};

export type InitialStateProp = {
  HeadLine: Field<string>;
  Teaser: Field<string>;
  PublishDate: Field<string>;
  ReadTime: Field<string>;
  Category: unknown;
  Type: Field<string>;
  MediaUrl: ImageField;
  MediaUrlTablet: ImageField;
  url: Field<string>;
};

export const card = tv({
  slots: {
    categoryStripe: [
      'after:block',
      'after:mt-3',
      'after:sm:mt-4',
      'after:w-12',
      'after:md:w-13.5',
      'after:h-1',
    ],
  },
  variants: {
    categoryType: {
      'category-1': {
        categoryStripe: 'after:bg-light-other-yourBusiness',
      },
      'category-2': {
        categoryStripe: 'after:bg-light-other-perspectives',
      },
      'category-3': {
        categoryStripe: 'after:bg-light-other-community',
      },
      'category-4': {
        categoryStripe: 'after:bg-light-other-yourMoney',
      },
      'bokf-red': {
        categoryStripe: 'after:bg-light-background-secondary-brand',
      },
    },
  },
});

const FeaturedResource = (props: RelatedResourcesProps): JSX.Element => {
  const initialState: InitialStateProp = {
    HeadLine: { value: '' },
    Teaser: { value: '' },
    PublishDate: { value: '' },
    ReadTime: { value: '' },
    Category: {},
    Type: { value: '' },
    MediaUrl: { value: { src: '' } },
    MediaUrlTablet: { value: { src: '' } },
    url: { value: '' },
  };

  const { sitecoreContext } = useSitecoreContext();
  const [leftNavExists, setLeftNavExists] = useState(false);

  // Check if LHN is present on the page
  useEffect(() => {
    const exists = document.getElementById('sidebar-navigation')?.hasChildNodes() || false;
    setLeftNavExists(exists);
  }, []);

  const { site } = sitecoreContext;
  const matchSiteName = 'Journalism';

  const { categoryStripe } = card();

  const [renderObj, setRenderObj] = useState(initialState);

  const dict = useDictionary();

  const watchMoreText = dict?.['Feature.Resource.WatchMore'] || 'Watch More';
  const readMoreText = dict?.['Feature.Resource.ReadMore'] || 'Read More';

  const pageType: PageTypes | undefined = (() => {
    for (const [, { templateId, templateName }] of Object.entries(
      SitecoreIds?.FeaturedResource?.Route
    )) {
      if (templateId === sitecoreContext?.route?.templateId) {
        return templateName;
      }
    }
    return undefined;
  })() as PageTypes;
  const pathName = usePathname();
  useEffect(() => {
    if (props?.rendering?.dataSource) {
      const stripColor: CategoryType =
        site?.name != matchSiteName
          ? ('bokf-red' as CategoryType)
          : (props?.fields?.Category?.[0]?.fields?.Color?.fields?.Value?.value as CategoryType) ||
            ('bokf-red' as CategoryType);

      const updatedState = {
        ...initialState, // Spread the existing state to keep unchanged values
        HeadLine: props?.fields?.Headline,
        Teaser: props?.fields?.Teaser,
        Category: categoryStripe({
          categoryType: stripColor,
        }),
        ReadTime: props?.fields?.ReadTime,
        Type: props?.fields?.ContentType?.fields?.Name,
        MediaUrlTablet: {
          value: {
            src: props?.fields?.HeroTablet?.value?.src,
          },
        },
        MediaUrl: {
          value: {
            src: props?.fields?.Hero?.value?.src,
          },
        },
        url: props?.fields?.url,
        PublishDate: props?.fields?.PublishDate,
      };
      setRenderObj(updatedState);
    } else {
      (async () => {
        //#region Prepare related resources request
        const relatedResourcesRequest = new Request();

        let language = 'en';

        if (typeof document !== 'undefined') {
          language = document.getElementsByTagName('html')[0].getAttribute('lang') || language;
        }
        const resolvedLanguage = (language in LOCALE_CONFIG ? language : 'en') as SupportedLanguage;

        const country = LOCALE_CONFIG[resolvedLanguage];

        relatedResourcesRequest.setContext({
          locale: {
            language,
            country,
          },
        });

        const sources = process.env.RESOURCES_SEARCH_SOURCE_ID;

        const relatedResourcesWidget = new WidgetRequest('related_resources');
        relatedResourcesWidget.setEntity('content');
        relatedResourcesWidget.setSearchLimit(parseInt('1'));
        relatedResourcesWidget.setSearchContent({});
        relatedResourcesWidget.setSources(sources?.split('|') as string[]);

        if (pageType) {
          relatedResourcesWidget.setSearchFilter(
            new FilterAnyOf('category', [sitecoreContext?.route?.name])
          );
        }

        relatedResourcesRequest.addWidgetItem(relatedResourcesWidget.toJson());
        try {
          let _relatedResources = await DataProvider.get(relatedResourcesRequest.toJson());

          // Sanitize results if any tokenized results
          if (sitecoreContext.tokenMappings) {
            _relatedResources = replaceTokens(
              _relatedResources as unknown as SourceObject,
              sitecoreContext.tokenMappings as TokenObject
            ) as unknown as SearchResponse;
          }

          const category = _relatedResources?.widgets?.[0]?.content?.[0]['category_color'];

          const stripColor: CategoryType =
            site?.name != matchSiteName
              ? 'bokf-red'
              : (category && Array.isArray(category) && category.length > 0 && category[0]) ||
                ('bokf-red' as CategoryType);

          // Create a new state object based on initialState
          const updatedState = {
            ...initialState, // Spread the previous state to avoid mutation
            HeadLine: { value: _relatedResources?.widgets?.[0]?.content?.[0]['name'] },
            Teaser: { value: _relatedResources?.widgets?.[0]?.content?.[0]['description'] },
            Category: categoryStripe({ categoryType: stripColor }),
            ReadTime: {
              value: _relatedResources?.widgets?.[0]?.content?.[0]['article_time'],
            },
            Type: { value: _relatedResources?.widgets?.[0]?.content?.[0]['type'] },
            MediaUrl: {
              value: {
                src: _relatedResources?.widgets?.[0]?.content?.[0]['image_url'],
              },
            },
            PublishDate: {
              value: new Date(
                _relatedResources?.widgets?.[0]?.content?.[0]['publish_date']
              ).toString(),
            },
            url: { value: _relatedResources?.widgets?.[0]?.content?.[0]['url'] },
          };

          // Use setRenderObj to update the state immutably
          setRenderObj(updatedState);
        } catch (error) {
          console.log('Error in fetching related resources', error);
        }
      })();
    }
  }, [pathName, sitecoreContext?.route?.name]);

  const displayOptions = (props?.params['DisplayOptions'] as unknown as DisplayOptions)?.Value
    ?.value;

  const imageBleed = props?.params?.ImageBleed || '';
  const hasBackground = !displayOptions?.includes('hero--noBkgd');
  const imageLeft = displayOptions?.includes('hero--imgLeft');

  if (!renderObj?.url?.value) return <></>;

  return (
    <section
      className={classNames(
        'container-wide featured-resource',
        { 'spacing-md': site?.name !== matchSiteName },
        { 'featured-LHN': leftNavExists },
        pageType ? 'mt-4 md:mt-12 lg:mt-8 featured-subPage' : 'featured-homePage'
      )}
    >
      <WidgetWrapper>
        <div
          className={`relative shadow-small md:shadow-none ${
            hasBackground
              ? 'md:border md:border-light-stroke-primary bg-light-background-neutral-variant'
              : 'md:border md:border-light-stroke-primary'
          }`}
        >
          <div
            className={classNames(
              'flex flex-col md:gap-8 featured-resource-container',
              imageLeft ? 'image-left' : 'image-right',
              leftNavExists ? 'lg:flex-col xl:flex-row' : 'lg:flex-row'
            )}
          >
            <div
              className={`flex flex-col justify-center ${
                imageLeft
                  ? 'order-last basis-1/2 py-6 md:py-8 xl:pt-16 3xl:pt-12 gap-y-4 md:gap-y-8'
                  : 'basis-1/2 md:order-first py-6 md:py-8 gap-y-4 md:gap-y-8'
              }`}
            >
              <RichTextA11yWrapper
                className={`${categoryStripe()} ${renderObj.Category} w-full relative rtaw`}
                tag="h2"
                field={renderObj?.HeadLine}
              />
              <ImageWrapper
                height="400"
                width="600"
                className={classNames(
                  'w-full h-full block',
                  imageBleed == '1'
                    ? 'object-cover mt-0 mb-0 @5xl:-mt-8 @5xl:mb-8'
                    : 'object-contain',
                  leftNavExists ? 'xl:hidden' : 'lg:hidden'
                )}
                field={{
                  value: {
                    src: renderObj?.MediaUrlTablet?.value?.src || renderObj?.MediaUrl?.value?.src,
                  },
                }}
              />
              <div className="flex flex-col gap-y-4 md:gap-y-3">
                <RichTextA11yWrapper
                  className="p1 text-light-fill-primary-variant rtaw"
                  field={renderObj?.Teaser}
                />
                <div className="flex justify-between">
                  <div>
                    <span className="relative c1 after:absolute after:-right-2 after:h-[15px] after:top-1/2 after:-translate-y-1/2 after:w-[1px] after:bg-strokes">
                      {formatTimestamp(renderObj?.PublishDate?.value, 'Month DD, YYYY')}
                    </span>
                    <span className="c1 ml-4">{renderObj?.ReadTime?.value}</span>
                  </div>
                  <div className="flex items-center justify-center md:hidden">
                    <SVG
                      svg="icon-arrow-right"
                      className="[&_path]:fill-light-fill-secondary [&_svg]:h-4.5 [&_svg]:w-4"
                    />
                  </div>
                </div>
              </div>
              <div className="relative hidden md:block">
                <Button
                  type="auxiliary"
                  tag="a"
                  href={renderObj?.url?.value}
                  label={renderObj?.Type?.value == 'Article' ? readMoreText : watchMoreText}
                />
              </div>
            </div>

            <div className={classNames('basis-1/2 hidden lg:block')}>
              <ImageWrapper
                height="400"
                width="600"
                className={classNames(
                  'w-full h-full',
                  imageBleed == '1'
                    ? 'object-cover mt-0 mb-0 @5xl:-mt-8 @5xl:mb-8'
                    : 'object-contain',
                  leftNavExists ? 'hidden xl:block' : 'hidden lg:block'
                )}
                field={{ value: { src: renderObj?.MediaUrl?.value?.src } }}
              />
            </div>
          </div>

          {/* Absolute div for mobile redirection */}
          <LinkWrapper
            className="absolute inset-0 z-10 md:hidden cursor-pointer"
            field={{ value: { href: renderObj.url?.value } }}
          ></LinkWrapper>
        </div>
      </WidgetWrapper>
    </section>
  );
};

export default FeaturedResource;
